/**
 * JournalStatsView.tsx
 * Extracted from JournalPanel.tsx — the stats tab content.
 * KPI grid + performance-by-setup table + performance-by-regime table.
 */
import React from 'react';
import { JournalStats } from '../hooks/useJournalState';

interface Props {
  stats: JournalStats;
}

export function JournalStatsView({ stats }: Props) {
  const { winRate, avgWin, avgLoss, totalPL, avgR, expectancy, streak, bySetup, byRegime } = stats;

  return (
    <>
      <div className="g4 mb12" style={{ gap: 6 }}>
        {[
          { l: 'Win Rate',   v: winRate ? `${winRate}%` : '—',   c: winRate ? (parseFloat(winRate) >= 60 ? 'var(--green)' : parseFloat(winRate) < 40 ? 'var(--red)' : 'var(--amber)') : 'var(--t1)' },
          { l: 'Avg Win',    v: avgWin ? `+$${avgWin}` : '—',    c: 'var(--green)' },
          { l: 'Avg Loss',   v: avgLoss ? `$${avgLoss}` : '—',   c: 'var(--red)' },
          { l: 'Total P&L',  v: `${totalPL >= 0 ? '+' : ''}$${totalPL.toFixed(2)}`, c: totalPL >= 0 ? 'var(--green)' : 'var(--red)' },
          { l: 'Expectancy', v: expectancy !== null ? `${Number(expectancy) >= 0 ? '+' : ''}$${expectancy}` : '—', c: expectancy !== null && Number(expectancy) >= 0 ? 'var(--green)' : 'var(--red)' },
          { l: 'Streak',     v: streak.count > 0 ? `${streak.count} ${streak.type === 'win' ? '🟢' : '🔴'}` : '—', c: streak.type === 'win' ? 'var(--green)' : 'var(--red)' },
          { l: 'Avg R',      v: avgR ? `${parseFloat(avgR) > 0 ? '+' : ''}${avgR}R` : '—', c: avgR && parseFloat(avgR) > 0 ? 'var(--green)' : 'var(--red)' },
          { l: 'Trades',     v: stats.completed.length, c: 'var(--t1)' },
        ].map(({ l, v, c }) => (
          <div key={l} className="card-inner" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 3 }}>{l}</div>
            <div className="mono" style={{ fontSize: 15, fontWeight: 700, color: String(c) }}>{v}</div>
          </div>
        ))}
      </div>

      {bySetup.length > 0 && (
        <>
          <div className="sl">Performance by Setup</div>
          <div style={{ overflowX: 'auto', marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--b0)' }}>
                  {['Setup', 'Trades', 'Win %', 'Avg P&L', 'Avg R'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Setup' ? 'left' : 'right', padding: '4px 6px', fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', fontFamily: 'var(--sans)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bySetup.map(row => (
                  <tr key={row.setup} style={{ borderBottom: '1px solid var(--b0)' }}>
                    <td style={{ padding: '5px 6px', color: 'var(--t1)' }}>{row.setup}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: 'var(--t2)' }}>{row.count}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: parseFloat(row.winRate) >= 60 ? 'var(--green)' : parseFloat(row.winRate) < 40 ? 'var(--red)' : 'var(--amber)' }}>{row.winRate}%</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: parseFloat(row.avgPL) >= 0 ? 'var(--green)' : 'var(--red)' }}>{parseFloat(row.avgPL) >= 0 ? '+' : ''}${row.avgPL}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: row.avgR !== '—' && parseFloat(row.avgR) > 0 ? 'var(--green)' : 'var(--red)' }}>{row.avgR}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {byRegime.length > 0 && (
        <>
          <div className="sl">Performance by Regime</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--b0)' }}>
                  {['Regime', 'Trades', 'Win %', 'Avg P&L'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Regime' ? 'left' : 'right', padding: '4px 6px', fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', fontFamily: 'var(--sans)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byRegime.map(row => (
                  <tr key={row.regime} style={{ borderBottom: '1px solid var(--b0)' }}>
                    <td style={{ padding: '5px 6px', color: 'var(--t1)' }}>{row.regime}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: 'var(--t2)' }}>{row.count}</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: parseFloat(row.winRate) >= 60 ? 'var(--green)' : parseFloat(row.winRate) < 40 ? 'var(--red)' : 'var(--amber)' }}>{row.winRate}%</td>
                    <td className="mono" style={{ textAlign: 'right', padding: '5px 6px', color: parseFloat(row.avgPL) >= 0 ? 'var(--green)' : 'var(--red)' }}>{parseFloat(row.avgPL) >= 0 ? '+' : ''}${row.avgPL}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {stats.completed.length === 0 && (
        <div className="empty">
          <div style={{ fontSize: 24, opacity: .3 }}>📊</div>
          <h3 style={{ fontSize: 14 }}>No Completed Trades</h3>
          <p>Log trades with exit prices to generate statistics.</p>
        </div>
      )}
    </>
  );
}
